import React, { useEffect, useState } from "react";
import { supabase } from "services/supabaseClient";
import { useTenant } from "context/TenantContext";

export default function AdminView({ onUploadSuccess }) {
  const [, setDocs] = useState([]);
  const [showForm, setShowForm] = useState(false);

  // Form state
  const [title, setTitle] = useState("");
  const [docType, setDocType] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);

  const [selectedOrg, setSelectedOrg] = useState(null);
  const [parties, setParties] = useState([]);
  const [selectedParties, setSelectedParties] = useState([]);
  const [trades, setTrades] = useState([]);
  const [selectedTrades, setSelectedTrades] = useState([]);

  // Lookup
  const [orgs, setOrgs] = useState([]);

  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const { tenant } = useTenant();

  // === Fetch docs ===
  async function fetchDocs() {
    const { data, error } = await supabase
      .from("record_docs")
      .select("id, title, doc_type, created_at")
      .order("created_at", { ascending: false });
    if (!error) setDocs(data || []);
  }

  // Fetch orgs once
  async function fetchOrgs() {
    const { data, error } = await supabase
      .from("orgs")
      .select("id, name")
      .order("name");
    if (error) {
      console.error("[RecordRoom/AdminView] Orgs fetch error:", error);
    } else {
      setOrgs(data || []);
    }
  }

  // === Effects ===
  useEffect(() => {
    fetchDocs();
    fetchOrgs();
  }, []);

  // Listen for global refresh events (triggered by OrgView deletes, etc.)
  useEffect(() => {
    const handler = () => fetchDocs();
    document.addEventListener("refresh-org-docs", handler);
    return () => document.removeEventListener("refresh-org-docs", handler);
  }, []);

  useEffect(() => {
    if (selectedOrg) {
      fetchPartiesForOrg(selectedOrg);
    }
  }, [selectedOrg]);

  useEffect(() => {
    if (selectedParties.length > 0) {
      fetchTradesForParties(selectedParties);
    } else {
      setTrades([]);
      setSelectedTrades([]);
    }
  }, [selectedParties]);

  // === Party/Trade fetchers ===
  async function fetchPartiesForOrg(orgId) {
    setParties([]);
    setSelectedParties([]);
    setTrades([]);
    setSelectedTrades([]);

    if (!orgId) return;

    const { data, error } = await supabase
      .from("org_party_links")
      .select("party_id, parties(canon_name)")
      .eq("org_id", orgId);

    if (error) {
      console.error("[RecordRoom/AdminView] Parties fetch error:", error);
      return;
    }

    const mapped = (data || []).map((row) => ({
      id: row.party_id,
      canon_name: row.parties?.canon_name,
    }));
    setParties(mapped);
  }

  async function fetchTradesForParties(partyIds) {
    setTrades([]);
    setSelectedTrades([]);

    if (!partyIds || partyIds.length === 0) return;

    const { data: partyData, error: partyError } = await supabase
      .from("parties")
      .select("id, canon_name")
      .in("id", partyIds);

    if (partyError) {
      console.error("[RecordRoom/AdminView] Party lookup error:", partyError);
      return;
    }

    const canonNames = (partyData || []).map((p) => p.canon_name);
    if (canonNames.length === 0) return;

    const { data, error } = await supabase
      .from("trades")
      .select("id, trade_id, leg1_party, leg1_party_canon, leg2_party")
      .in("leg1_party_canon", canonNames)
      .order("trade_id", { ascending: true });

    if (error) {
      console.error("[RecordRoom/AdminView] Trades fetch error:", error);
      return;
    }

    setTrades(data || []);
  }

  // === Upload handler ===
  const handleUpload = async (e) => {
    e.preventDefault();
    setError("");

    if (!file || !title || !docType || !selectedOrg) {
      setError("Title, doc type, file, and org are required.");
      return;
    }

    if (!tenant?.id) {
      setError("No active tenant selected.");
      return;
    }

    setUploading(true);

    try {
      // 1. Upload file
      const filePath = `${Date.now()}-${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from("record_docs_files")
        .upload(filePath, file);
      if (uploadError) throw uploadError;

      // 2. Insert record_docs
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData?.user?.id;

      const payload = {
        title,
        doc_type: docType,
        description,
        file_path: filePath,
        created_by: userId,
        org_id: selectedOrg,
        tenant_id: tenant.id,
      };

      const { data: newDoc, error: docError } = await supabase
        .from("record_docs")
        .insert([payload])
        .select()
        .single();

      if (docError) throw docError;

      // 3. Insert record_doc_scopes
      const scopeRows = [
        ...selectedParties.map((p) => ({ doc_id: newDoc.id, party_id: p })),
        ...selectedTrades.map((t) => ({ doc_id: newDoc.id, trade_id: t })),
      ];
      if (scopeRows.length > 0) {
        const { error: scopeError } = await supabase
          .from("record_doc_scopes")
          .insert(scopeRows);
        if (scopeError) throw scopeError;
      }

      // Reset + refresh
      await fetchDocs();
      if (onUploadSuccess) {
        onUploadSuccess();
      }

      setShowForm(false);
      setTitle("");
      setDocType("");
      setDescription("");
      setFile(null);
      setSelectedOrg(null);
      setParties([]);
      setSelectedParties([]);
      setTrades([]);
      setSelectedTrades([]);
    } catch (err) {
      console.error("[RecordRoom/AdminView] Upload failed:", err);
      setError("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      {!showForm ? (
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>
          Upload New Document
        </button>
      ) : (
        <form
          onSubmit={handleUpload}
          className="space-y-3 border p-4 rounded bg-app"
        >
          {error && <div className="text-red-600 text-sm">{error}</div>}
          {/* Title */}
          <div>
            <label className="block text-sm text-muted mb-1">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border rounded p-2 input-app"
            />
          </div>
          {/* Doc Type */}
          <div>
            <label className="block text-sm text-muted mb-1">Doc Type</label>
            <select
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              className="w-full border rounded p-2 input-app"
            >
              <option value="">Select type</option>
              <option value="isda">ISDA (Master/Schedule/CSA)</option>
              <option value="confirmation">Confirmation</option>
              <option value="fair_value_opinion">Fair Value Opinion</option>
              <option value="valuation_report">Valuation Report</option>
              <option value="consolidated_report">Consolidated Report</option>
              <option value="policy">Policy</option>
              <option value="other">Other</option>
            </select>
          </div>
          {/* Description */}
          <div>
            <label className="block text-sm text-muted mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full border rounded p-2 input-app"
            />
          </div>
          {/* File */}
          <div>
            <label className="block text-sm text-muted mb-1">File</label>
            <input
              type="file"
              onChange={(e) => setFile(e.target.files[0])}
              className="w-full"
            />
          </div>
          {/* Org selector */}
          <div>
            <label className="block text-sm text-muted mb-1">Org</label>
            <select
              value={selectedOrg || ""}
              onChange={(e) => setSelectedOrg(e.target.value || null)}
              className="w-full border rounded p-2 input-app"
            >
              <option value="">Select organization</option>
              {orgs.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          {/* Parties */}
          {selectedOrg && (
            <div>
              <label className="block text-sm text-muted mb-1">Parties</label>
              <select
                multiple
                value={selectedParties}
                onChange={(e) =>
                  setSelectedParties(
                    Array.from(e.target.selectedOptions, (o) => o.value)
                  )
                }
                className="w-full border rounded p-2 input-app h-32"
              >
                {parties.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.canon_name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {/* Trades */}
          {selectedParties.length > 0 && (
            <div>
              <label className="block text-sm text-muted mb-1">Trades</label>
              <select
                multiple
                value={selectedTrades}
                onChange={(e) =>
                  setSelectedTrades(
                    Array.from(e.target.selectedOptions, (o) => o.value)
                  )
                }
                className="w-full border rounded p-2 input-app h-32"
              >
                {trades.length === 0 ? (
                  <option disabled>No trades found</option>
                ) : (
                  trades.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.trade_id} — {t.leg1_party} vs {t.leg2_party}
                    </option>
                  ))
                )}
              </select>
            </div>
          )}
          {/* Actions */}
          <div className="flex gap-2">
            <button type="submit" className="btn btn-primary" disabled={uploading}>
              {uploading ? "Uploading..." : "Submit"}
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setShowForm(false)}
              disabled={uploading}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
